import React, {Component} from 'react';
import _ from 'lodash';
import {Link} from 'react-router-dom';
import C from 'util/Calculations';
import Api from 'util/Api';
import WideRoomCard from 'components/WideRoomCard';
import {
    sectionDataTooltip,
    filterPaRTooltip,
    filterWarningTooltip,
    filterUrgentTooltip,
    filterAllTooltip
} from "components/Tooltips/FacilitiesSectionsTooltips";
import {WhisperWrapper} from "components/Tooltips/WhisperWrapper";

const [ERROR, WARNING, INFO] = [0, 1, 2];

class SectionShow extends Component {
    constructor(props) {
        super(props);

        this.state = {
            rooms: {},
            isLoading: true,
            selectedSection: props.match.params.section,
            filters: {
                wing: '_all',
                searchTerm: '',
                category: 'all',
            },
        };

        this.handleSectionChange = this.handleSectionChange.bind(this);
        this.handleCategoryChange = this.handleCategoryChange.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.calculateRoomStatus = this.calculateRoomStatus.bind(this);
    }

    componentDidMount() {
        this._mounted = true;
        const {section} = this.props.match.params;

        const sectionDetails = {
            sectionName: section,
        };
        this.props.auth.onSectionDetails(sectionDetails);
        this.setState(sectionDetails);

        // Get beds
        Api.withAuth(this.props.auth)
            .getBedsWithPatients(this.props.match.params)
            .then((bedsResponse) => {
                this.setState({beds: bedsResponse});
                this.setState({isLoading: false})
            })
            .catch((err) => {
                console.log(err);
                this.setState({isLoading: false, beds: []})}
            )
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.match.params.section !== this.props.match.params.section) {
            this.setState({selectedSection: this.props.match.params.section});
        }
    }

    handleSectionChange(event) {
        const section = event.target.value;
        this.props.history.push(
            `/facilities/${this.props.match.params.facility}/sections/${section}/rooms`
        );
        this.props.onSectionChange(section);
        return true;
    }

    handleCategoryChange(category) {
        this.setState({
            filters: {
                ...this.state.filters,
                category: category,
            },
        });
    }

    handleFilterChange(event) {
        this.setState({
            filters: {
                ...this.state.filters,
                [event.target.name]: event.target.value,
            },
        });
    }

    roomAndWingName(roomNameWithWing) {
        // Name format is "Wing/Room" or just "Room"
        try {
            const parts = roomNameWithWing.split('/').map((part) => part.trim());

            return {
                wing: parts[1] ? parts[0] : null,
                roomName: parts[1] || parts[0],
            };
        } catch (err) {
            return {};
        }
    }

    calculateRoomStatus(room) {
        const {active_alerts} = room;

        let statuses = [];
        let hasVisitStatus = false;

        if (active_alerts) {
            active_alerts.map((alert) => {
                if (alert.type === 'visit' && alert.subtype === 'visit_soon') {
                    statuses.push({
                        level: INFO,
                        message: 'Visit Soon',
                    });
                } else if (
                    alert.type === 'visit' &&
                    alert.subtype === 'require_visit'
                ) {
                    hasVisitStatus = true;
                    statuses.push({
                        level: INFO,
                        message: 'Visit Now',
                    });
                } else if (
                    alert.type === 'respiration' &&
                    alert.subtype === 'high_urgent'
                ) {
                    statuses.push({
                        level: ERROR,
                        message: 'High RR',
                    });
                } else if (
                    alert.type === 'respiration' &&
                    alert.subtype === 'high_warning'
                ) {
                    statuses.push({
                        level: WARNING,
                        message: 'High RR',
                    });
                } else if (
                    alert.type === 'respiration' &&
                    alert.subtype === 'low_urgent'
                ) {
                    statuses.push({
                        level: ERROR,
                        message: 'Low RR',
                    });
                } else if (
                    alert.type === 'respiration' &&
                    alert.subtype === 'low_warning'
                ) {
                    statuses.push({
                        level: WARNING,
                        message: 'Low RR',
                    });
                } else if (alert.type === 'bedexit') {
                    statuses.push({
                        level: ERROR,
                        message: 'Bed Exit',
                    });
                }
                return null;
            });
        }
        statuses = _.sortBy(statuses, 'level');

        return {
            statuses,
            hasError: _.filter(statuses, {level: ERROR}).length > 0,
            hasVisitStatus: hasVisitStatus,
            hasWarning: _.filter(statuses, {level: WARNING}).length > 0,
        };
    }

    render() {
        const {auth, match, sections} = this.props;
        const facility = this.props.auth.facilityDetails.facilityName;
        const {filters, beds} = this.state;
        if (!this.state.isLoading && (!beds || beds.length === 0)) {
            return (
                <div>No rooms found for this section</div>
            )
        }

        const validRooms = _(beds)
            .mapValues((room) => ({
                ...room,
                ...this.roomAndWingName(_.get(room, 'name')),
            }))
            .mapValues((room) => ({
                ...room,
                respiration: _.defaultTo(room.respiration, []),
                movements: _.defaultTo(room.movements, []),
                thresholds: auth.facilityDetails.thresholds,
            }))
            .mapValues((room) => ({
                ...room,
                ...C.movementStats(room.movements),
                minutesUntilNextVisit: C.minutesUntilNextVisit(room),
                minutesSinceLastVisit: Math.round(
                    (Date.now() - room.last_visit * 1000) / 1000 / 60
                ),
            }))
            .mapValues((room) => ({
                ...room,
                ...this.calculateRoomStatus(room),
            }))
            .value();

        const roomCount = _.size(validRooms);
        const occupiedRoomCount = _(beds).pickBy('patient_id').size();

        let filteredRooms = _(validRooms)
            .filter(
                (room) =>
                    filters.wing === '_all' ||
                    _.get(room, 'name', '').startsWith(filters.wing)
            )
            .filter(
                (room) =>
                    filters.searchTerm.length === 0 ||
                    _.get(room, 'name', '')
                        .toLowerCase()
                        .includes(filters.searchTerm.toLowerCase()) ||
                    _.get(room, 'patient.firstName', '')
                        .toLowerCase()
                        .includes(filters.searchTerm.toLowerCase()) ||
                    _.get(room, 'patient.lastName', '')
                        .toLowerCase()
                        .includes(filters.searchTerm.toLowerCase())
            )
            .value();

        const atRiskCount = _.countBy(filteredRooms, 'patient.atRisk').true || 0;
        const criticalCount = _.countBy(filteredRooms, 'hasError').true || 0;
        const warningCount = _.countBy(filteredRooms, 'hasWarning').true || 0;
        // const visitCount = _.countBy(filteredRooms, 'hasVisitStatus').true || 0;

        const criticalClass = criticalCount > 0 ? 'danger' : 'info';
        // const visitClass = visitCount > 0 ? 'warning' : 'info';
        const warningClass = warningCount > 0 ? 'warning' : 'info';

        filteredRooms = _(filteredRooms)
            .filter(
                (room) =>
                    filters.category === 'all' ||
                    (filters.category === 'atRisk' && _.get(room, 'patient.atRisk')) ||
                    (filters.category === 'critical' && _.get(room, 'hasError')) ||
                    (filters.category === 'warning' && _.get(room, 'hasWarning')) ||
                    (filters.category === 'visits' && _.get(room, 'hasVisitStatus'))
            )
            .sortBy((r) => r.conditions ? r.conditions[0] === "lost connection" : '')
            .value();

        return (
            <section className='section-index'>
                <div className='subnav fixed-top'>
                    <div className='container h-100'>
                        <div className='row justify-content-end align-items-center h-100'>
                            <div className='col-md-6 mr-auto'>
                                <div className='section-nav'>
                                    <div>
                                        <strong>All Locations / {facility}</strong>
                                    </div>
                                    <div>
                                        <select
                                            style={{border: 0, background: 'none'}}
                                            value={this.state.selectedSection}
                                            onChange={this.handleSectionChange}
                                        >
                                            {_.map(sections, (section) => (
                                                <option key={section} value={section}>
                                                    {section}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className='room-count'>
                                <small>Rooms Occupied:</small>
                                <strong>
                                    {occupiedRoomCount}/{roomCount}
                                </strong>
                            </div>

                            <div className='actions'>

                                <Link
                                    to={`/facilities/${match.params.facility}/sections/${match.params.section}/neighborhood-data`}
                                >
                                    <WhisperWrapper
                                        tooltip={sectionDataTooltip}
                                        props={{placement: "bottom", controlId: "control-id-hover", trigger: "hover"}}
                                    >
                                        <span>Section Data</span>
                                    </WhisperWrapper>
                                </Link>

                            </div>
                        </div>
                    </div>
                </div>

                <div className='container room-search-actions-wrap fixed-top'>
                    <div className='row'>
                        <div className='col-md-12 room-search-actions'>
                            {/*<select*/}
                            {/*    name="wing"*/}
                            {/*    className="custom-select"*/}
                            {/*    value={this.state.filters.wing}*/}
                            {/*    onChange={this.handleFilterChange}*/}
                            {/*>*/}
                            {/*    <option value="_all">All Wings</option>*/}
                            {/*    {wings.map((wing) => (*/}
                            {/*        <option key={wing} value={wing}>*/}
                            {/*            {wing}*/}
                            {/*        </option>*/}
                            {/*    ))}*/}
                            {/*</select>*/}

                            <input
                                type='search'
                                className='form-control'
                                placeholder='Search Room/Resident'
                                name='searchTerm'
                                value={this.state.searchTerm}
                                onChange={this.handleFilterChange}
                            />

                            <WhisperWrapper
                                tooltip={filterAllTooltip}
                                props={{placement: "bottom", controlId: "control-id-hover", trigger: "hover"}}
                            >
                                <button
                                    className={`btn ${
                                        filters.category === 'all' ? 'btn' : 'btn-outline'
                                    }-info`}
                                    onClick={() => this.handleCategoryChange('all')}
                                >
                                    All
                                </button>
                            </WhisperWrapper>

                            <WhisperWrapper
                                tooltip={filterUrgentTooltip}
                                props={{placement: "bottom", controlId: "control-id-hover", trigger: "hover"}}
                            >
                                <button
                                    className={`btn ${
                                        filters.category === 'critical' ? 'btn' : 'btn-outline'
                                    }-${criticalClass}`}
                                    onClick={() => this.handleCategoryChange('critical')}
                                >
                                    Urgent
                                    <span
                                        className={`badge badge-pill badge-${
                                            filters.category === 'critical' ? 'light' : criticalClass
                                        }`}
                                    >
           {criticalCount}
         </span>
                                </button>
                            </WhisperWrapper>

                            <WhisperWrapper
                                tooltip={filterWarningTooltip}
                                props={{placement: "bottom", controlId: "control-id-hover", trigger: "hover"}}
                            >
                                <button
                                    className={`btn ${
                                        filters.category === 'warning' ? 'btn' : 'btn-outline'
                                    }-${warningClass}`}
                                    onClick={() => this.handleCategoryChange('warning')}
                                >
                                    Warning
                                    <span
                                        className={`badge badge-pill badge-${
                                            filters.category === 'warning' ? 'light' : warningClass
                                        }`}
                                    >
           {warningCount}
         </span>
                                </button>
                            </WhisperWrapper>

                            <WhisperWrapper
                                tooltip={filterPaRTooltip}
                                props={{placement: "bottom", controlId: "control-id-hover", trigger: "hover"}}
                            >
                                <button
                                    className={`btn ${
                                        filters.category === 'atRisk' ? 'btn' : 'btn-outline'
                                    }-info`}
                                    onClick={() => this.handleCategoryChange('atRisk')}
                                >
                                    PaR
                                    <span
                                        className={`badge badge-pill badge-${
                                            filters.category === 'atRisk' ? 'light' : 'info'
                                        }`}
                                    >
           {atRiskCount}
         </span>
                                </button>
                            </WhisperWrapper>
                        </div>
                    </div>
                </div>

                <div className='container room-cards'>
                    {this._mounted &&
                    filters.category !== 'all' &&
                    filteredRooms.length === 0 ? (
                        <div className='filter-empty'>No {filters.category} Residents</div>
                    ) : (
                        _.chunk(filteredRooms, 3).map((roomRow, index) => (
                            <div key={index} className='row align-items-stretch'>
                                {roomRow.map((bed) => (
                                    <WideRoomCard
                                        auth={auth}
                                        key={`${index}-${bed.bed_id}`}
                                        card_key={`${index}-${bed.bed_id}`}
                                        id={bed.bed_id}
                                        match={this.props.match}
                                        history={this.props.history}
                                        {...bed}
                                    />
                                ))}
                            </div>
                        ))
                    )}
                </div>
            </section>
        );
    }
}

export default SectionShow;
